import { Field, FieldProps, Form, useFormikContext } from 'formik';
import { ErrorMessage, FileField, FormField, Label, MoneyField, SelectField, SubLabel } from '@round/whitelabel-ui';
import { useConditionalCurrencies } from 'contexts/OptionsContext/useConditionalCurrencies';
import styles from './PurchaseOrderForm.module.css';
import useBillingEntitySelect, {
    BillingEntityOption,
} from 'Modules/Plans/Finance/BillingEntities/hooks/useBillingEntitySelect';
import omit from 'lodash/omit';
import useTeamSelect from 'Modules/Plans/Team/hooks/useTeamSelect';
import { Input } from '@round/whitelabel-ui';
import { Checkbox } from '@round/whitelabel-ui';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { FormValues } from './helpers';
import { ValueType } from 'react-select';

const PurchaseOrderForm = () => {
    const { selectedClientId } = useClientSelect();
    const creatorbaseCurrencies = useConditionalCurrencies(true);

    const { getProps: getBillingEntitiesSelectProps } = useBillingEntitySelect({
        params: { client_id: selectedClientId },
    });
    const billingEntitiesSelectProps = getBillingEntitiesSelectProps();
    const { getProps: getTeamSelectProps } = useTeamSelect({
        initOn: 'menuOpen',
        params: { client_id: selectedClientId },
        enabled: typeof selectedClientId === 'number',
    });

    const { values, initialValues, setFieldValue, setErrors, validateForm } = useFormikContext<FormValues>();

    const handleSetIsInternal = async (isInternal: boolean) => {
        setFieldValue('isInternal', isInternal);

        if (isInternal === initialValues.isInternal) {
            setFieldValue('poNumber', initialValues.poNumber, false);
            setFieldValue('request_reference', initialValues.request_reference, false);
            // Validate the form with the initial values to ensure the errors are cleared or applied correctly.
            setErrors(
                await validateForm({
                    ...values,
                    poNumber: initialValues.poNumber,
                    request_reference: initialValues.request_reference,
                })
            );
        } else {
            setFieldValue('poNumber', '', false);
            setFieldValue('request_reference', '', false);
            // Don't need to validate the form here, since the fields are empty, so they can be validated on usual actions.
        }
    };

    const getInternalPlaceholder = (
        field: keyof Pick<FormValues, 'poNumber' | 'request_reference'>,
        fallback: string
    ) => (initialValues.isInternal ? initialValues[field] || fallback : fallback);

    const poNumberSubLabel = 'ⓘ At least one required: PO Number or Req. Reference';
    const isPoNumberRequired = !values.isInternal;
    return (
        <Form className={styles.form}>
            <SelectField
                required
                name="billingEntity"
                label="Billing Entity"
                {...omit(billingEntitiesSelectProps, ['value', 'onChange'])}
                placeholder="Select billing entity"
                onChange={(value: ValueType<BillingEntityOption, false>) => {
                    if (!value) {
                        return;
                    }

                    setFieldValue('isInternal', value.requires_purchase_order_number === 'not_required');
                }}
            />

            {!!values.billingEntity && (
                <>
                    <Field name="poNumber" label="PO No." placeholder="Enter PO number">
                        {({ field, ...props }: FieldProps<string, FormValues>) => (
                            <div className={styles.formField}>
                                <div className={styles.poCheckboxRow}>
                                    <Label htmlFor={field.name} isRequired={isPoNumberRequired}>
                                        PO No.
                                    </Label>

                                    {values.billingEntity?.requires_purchase_order_number === 'optional' && (
                                        <Label
                                            htmlFor="isInternal"
                                            className={styles.poCheckbox}
                                            onClick={() => handleSetIsInternal(!props.form.values.isInternal)}
                                        >
                                            <SubLabel>Client requires PO</SubLabel>
                                            <Checkbox
                                                id="isInternal"
                                                value={!props.form.values.isInternal}
                                                onChange={() => handleSetIsInternal(!props.form.values.isInternal)}
                                            />
                                        </Label>
                                    )}
                                </div>

                                {isPoNumberRequired && <SubLabel>{poNumberSubLabel}</SubLabel>}

                                <Input
                                    id={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    hasError={Boolean(props.meta.error && props.meta.touched)}
                                    placeholder={
                                        props.form.values.isInternal
                                            ? getInternalPlaceholder('poNumber', 'RPO-XXXXXXXX')
                                            : 'Enter PO number'
                                    }
                                    disabled={props.form.values.isInternal}
                                />
                                <ErrorMessage name={field.name} />
                            </div>
                        )}
                    </Field>
                    <Field name="request_reference" label="Req. Reference">
                        {({ field, ...props }: FieldProps<string, FormValues>) => (
                            <div className={styles.formField}>
                                <Label htmlFor={field.name} isRequired={isPoNumberRequired}>
                                    Req. Reference
                                </Label>

                                {isPoNumberRequired && <SubLabel>{poNumberSubLabel}</SubLabel>}

                                <Input
                                    id={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    hasError={Boolean(props.meta.error && props.meta.touched)}
                                    placeholder={
                                        props.form.values.isInternal
                                            ? getInternalPlaceholder('request_reference', 'RPOD-XXXXXXXX')
                                            : 'Enter request reference'
                                    }
                                    disabled={values.isInternal}
                                />
                                <ErrorMessage name={field.name} />
                            </div>
                        )}
                    </Field>

                    <div className={styles.row}>
                        <MoneyField
                            required
                            currencyFieldName="currency"
                            name="amount"
                            label="Amount"
                            currencies={creatorbaseCurrencies}
                        />

                        <FileField
                            required={!!values.billingEntity?.requires_file_upload}
                            name="fileUpload"
                            label="File Upload"
                            placeholder="Upload file"
                        />
                    </div>

                    <FormField
                        required
                        className={styles.description}
                        name="description"
                        component="textarea"
                        label="Description"
                        subLabel="ⓘ Necessary for invoice reference and line item description"
                        placeholder="Enter short description"
                    />

                    <SelectField
                        required
                        name="team"
                        label="Team"
                        {...omit(getTeamSelectProps(), ['value', 'onChange'])}
                        placeholder="Select team"
                    />
                </>
            )}
        </Form>
    );
};

export default PurchaseOrderForm;
